import { Component, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AbandonNoticeComponent } from '@shared/dialog-contents/abandon-notice/abandon-notice.component';
import { collectFormErrors } from '@shared/formHelpers';
import { Application, ApplicationSegment, ApplicationStatus, BundleInfo } from '@shared/models/data-model';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { BundleInfoService } from '@shared/services/bundle-info.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { GoogleTagManagerService } from '@shared/services/google-tag-manager.service';
import { ModalDialogService } from '@shared/services/modal-dialog.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID } from '@shared/services/steps.service';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';

const SUBSTEP_NAME = "Pending Applications";

interface ApplicationSelectionValue {
    isResume: boolean;
    id: string | null;
}

@Component({
    selector: 'app-applications',
    templateUrl: './applications.component.html',
    styleUrl: './applications.component.scss'
})
export class ApplicationsComponent implements OnInit {
    applicationControl: FormControl<ApplicationSelectionValue>;
    applicationList: Application[];
    applicationSelectionList: ApplicationSelectionValue[];
    readonly newApplicationValue: ApplicationSelectionValue = { isResume: false, id: null };
    newOfferName: string;
    bundles: BundleInfo[];
    isDirect = false;
    displayNewButton = false;
    displayAbandonAndChoosePlan = false;
    loading = false;

    constructor(
        private readonly registrationService: RegistrationService,
        private readonly bundleInfoService: BundleInfoService,
        private readonly modalService: ModalDialogService,
        private readonly amplitudeService: AmplitudeService,
        private readonly gtmService: GoogleTagManagerService,
        private readonly translateService: TranslateService,
        private readonly logger: NGXLogger,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService
    ) { }

    ngOnInit(): void {
        this.trackSubStepViewed();
        this.applicationControl = new FormControl<ApplicationSelectionValue>(null, Validators.required);
        const bundleList: BundleInfo[] = this.route.snapshot.data.authData.bundleInfoList;
        this.newOfferName = bundleList
            .find(bundle => bundle.plan === this.bundleInfoService.getNewApplicationPlan() && bundle.offer === this.bundleInfoService.getNewApplicationOffer())
            ?.description
        const applicationList: Application[] = this.route.snapshot.data.authData.applicationList;
        this.isDirect = applicationList.some(app => app.segment === ApplicationSegment.direct);
        if (!this.isDirect) {
            this.checkNewApplicationAvailability(applicationList);
        }
        applicationList.sort((app1, app2) => ('' + app1.company?.registeredName).localeCompare(app2.company?.registeredName));
        this.applicationList = applicationList;
        this.applicationSelectionList = applicationList.map(app => ({ isResume: true, id: app.id }))
        const applicationsInProgress = applicationList
            .filter(app => (app.segment === ApplicationSegment.direct && app.status === ApplicationStatus.Pending)
                || (app.segment === ApplicationSegment.selfServe
                    && [ApplicationStatus.Pending, ApplicationStatus.Completed, ApplicationStatus.ReviewInProgress].includes(app.status)));
        if (applicationsInProgress.length === 1 && !this.displayNewButton) {
            this.applicationControl.setValue(this.getApplicationSelectionValue(applicationsInProgress[0].id));
        }
    }

    getApplicationSelectionValue(id: string): ApplicationSelectionValue {
        return this.applicationSelectionList.find(app => app.id === id);
    }

    async loadApplication() {
        if (this.applicationControl.valid) {
            this.loading = true;
            this.trackSubStepCompleted();
            const application = this.applicationList.find(app => app.id === this.applicationControl.value.id);
            const resumeRoute = await this.registrationService.initRegistration(application);

            const applicationInitActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.APPLICATIONS_RESUME);

            if (application.webappCheckList?.split(';').length > 0) {
                this.gtmService.trackRegistrationResumed();
                this.amplitudeService.trackApplicationResumed(application.segment);
            } else {
                this.dynatrace.setActionName(applicationInitActionId, DTRUM_ACTION_NAME.APPLICATIONS_START_PREMADE);
                this.gtmService.trackNewRegistrationStarted(application.planName, application.offerName, application.segment);
                this.amplitudeService.trackNewRegistrationStarted(application.segment);
            }

            await this.router.navigate([resumeRoute]).then(() => {
                this.dynatrace.leaveAction(applicationInitActionId);
            });
        }
    }

    private checkNewApplicationAvailability(list: Application[]): void {
        const isSelfServe = list.every(app => app.segment === ApplicationSegment.selfServe);
        const noApplicationsInReviewOrCompleted = list
            .every(app => ![ApplicationStatus.Completed, ApplicationStatus.ReviewInProgress].includes(app.status));
        if (isSelfServe && noApplicationsInReviewOrCompleted) {
            const newPlanIsValid: boolean = this.route.snapshot.data.authData.newPlanIsValid;
            this.displayNewButton = newPlanIsValid;
            this.displayAbandonAndChoosePlan = !newPlanIsValid;
        }
    }

    async onSubmit(event: SubmitEvent | KeyboardEvent) {
        event.preventDefault();
        this.applicationControl.markAllAsTouched();
        const applicationSelection = this.applicationControl.value;

        if (!applicationSelection) {
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.applicationControl, { application: 'required' }));
            return;
        }
        if (applicationSelection.isResume) {
            await this.loadApplication();
        } else {
            this.newApplication();
        }
    }

    getOfferDescription(app: Application): Observable<string> {
        return this.bundleInfoService.getPlanOfferDescription(app.planName, app.offerName);
    }

    isAppDisabled(app: Application): boolean | null {
        if (app.segment === ApplicationSegment.selfServe && app.status === ApplicationStatus.Finalized) {
            return true;
        }
        if (app.segment === ApplicationSegment.direct && [ApplicationStatus.Completed, ApplicationStatus.ReviewInProgress, ApplicationStatus.Finalized].includes(app.status)) {
            return true;
        }
        return null;
    }

    private newApplication() {
        if (this.applicationList.every(app => app.status === ApplicationStatus.Finalized)) {
            this.startNewApplication();
        } else {
            this.modalService.showModalDialog(AbandonNoticeComponent,
                {
                    withReturnValue: true, title: "MODAL_CONTENTS.ABANDON_NOTICE.TITLE",
                    acceptLabel: "MODAL_CONTENTS.ABANDON_NOTICE.BUTTONS.START_NEW",
                    cancelLabel: "MODAL_CONTENTS.ABANDON_NOTICE.BUTTONS.GO_BACK",
                    customClass: "abandon-notice-dialog"
                }).subscribe(startNew => {
                    if (startNew) {
                        this.startNewApplication(true);
                    }
                });
        }
    }

    private startNewApplication(abandonPartials:boolean = false) {
        this.loading = true;
        const startNewActionId = this.dynatrace.enterAction(abandonPartials ? DTRUM_ACTION_NAME.APPLICATIONS_ABANDON : DTRUM_ACTION_NAME.APPLICATIONS_START_NEW);

        this.registrationService.startApplication({
            email: this.applicationList[0].applicant.email,
            browserLanguage: this.translateService.getCurrentLang(),
            planName: this.bundleInfoService.getNewApplicationPlan(),
            offerName: this.bundleInfoService.getNewApplicationOffer()
        }).then(async (application) => {
            this.trackSubStepCompleted();
            this.amplitudeService.trackApplicationAbandoned();
            const route = await this.registrationService.initRegistration(application);
            this.gtmService.trackNewRegistrationStarted(application.planName, application.offerName, application.segment);
            this.amplitudeService.trackNewRegistrationStarted(application.segment);

            await this.router.navigate([route]).then(() => {
                this.dynatrace.leaveAction(startNewActionId);
            });
        }).catch((error) => {
            this.logger.error(error);
            this.loading = false;
        });
    }

    abandon() {
        const tracking = this.amplitudeService.trackBackToPlanSelection(this.bundleInfoService.getNewApplicationPlan(), this.bundleInfoService.getNewApplicationOffer());
        this.registrationService.redirectToPlanSelection(tracking.promise);
    }

    getStepLabel(app: Application): string {
        let step: string;
        const checklist = app.webappCheckList?.split(';') || [];
        const uboChoiceEnabled = this.registrationService.isUboChoiceStepEnabled(app.company?.eMoneyLicenseIssuer);
        const uboDetailsEnabled = this.registrationService.isUboDetailsStepEnabled(uboChoiceEnabled, app.uboProvisioningChoice);
        if (this.isApplicationCompleted(app)) {
            step = 'SUBMITTED';
        } else if ([ApplicationStatus.Completed, ApplicationStatus.ReviewInProgress].includes(app.status)) {
            step = 'VERIFY_IDENTITY';
        } else if (checklist.includes(REGISTRATION_STEP_ID.EXTERNAL_DEPOSIT)) {
            step = 'TERMS_AND_CONDITIONS';
        } else if (checklist.includes(REGISTRATION_STEP_ID.UBO_DETAILS)) {
            step = 'EXPECTED_ACTIVITY';
        } else if (checklist.includes(REGISTRATION_STEP_ID.UBO_CHOICE)) {
            step = (uboDetailsEnabled) ? 'UBO' : 'EXPECTED_ACTIVITY';
        } else if (checklist.includes(REGISTRATION_STEP_ID.ADMIN)) {
            step = (uboChoiceEnabled) ? 'UBO' : 'EXPECTED_ACTIVITY';
        } else if (checklist.includes(REGISTRATION_STEP_ID.DIRECTOR)) {
            step = 'ADMIN';
        } else if (checklist.includes(REGISTRATION_STEP_ID.BUSINESS_DESCRIPTION)) {
            step = 'LEGAL_REPRESENTATIVE';
        } else if (checklist.includes(REGISTRATION_STEP_ID.SIGNUP)) {
            step = 'COMPANY_INFO';
        } else {
            step = 'ABOUT_YOU';
        }
        return `AUTH.APPLICATIONS.STEP.${step}`;
    }

    private isApplicationCompleted(app: Application) {
        return app.status === ApplicationStatus.Finalized ||
            (app.segment === ApplicationSegment.direct && [ApplicationStatus.Completed, ApplicationStatus.ReviewInProgress].includes(app.status));
    }

    private trackSubStepViewed(): void {
        this.amplitudeService.trackSubStepViewed(SUBSTEP_NAME);
        this.gtmService.trackSubStepViewed(SUBSTEP_NAME);
    }

    private trackSubStepCompleted(): void {
        this.amplitudeService.trackSubStepCompleted(SUBSTEP_NAME);
        this.gtmService.trackSubStepCompleted(SUBSTEP_NAME);
    }
}
